/*
  Yard Automation Pros: product page interactions.
  Plain JS, no GSAP dependency, so every control works even if motion is off.
  Components: tabs, spec search, system builder, lightbox, sticky add to cart,
  autoplay-on-view videos, count-up numbers.
*/
(function () {
  if (window.__yapProduct) return;
  window.__yapProduct = true;

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var phone = window.matchMedia("(max-width: 749px)").matches;
  var money = function (cents) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(cents / 100);
  };

  /* ---------- Tabs: [data-yap-tabs] > [role=tab] + [role=tabpanel] ---------- */
  function initTabs(root) {
    root.querySelectorAll("[data-yap-tabs]:not([data-yap-ready])").forEach(function (wrap) {
      wrap.setAttribute("data-yap-ready", "");
      var tabs = [].slice.call(wrap.querySelectorAll('[role="tab"]'));
      var panels = [].slice.call(wrap.querySelectorAll('[role="tabpanel"]'));
      function select(i, focus) {
        tabs.forEach(function (t, n) {
          var on = n === i;
          t.setAttribute("aria-selected", on ? "true" : "false");
          t.tabIndex = on ? 0 : -1;
          if (panels[n]) {
            panels[n].hidden = !on;
            if (on) {
              panels[n].classList.remove("is-entering");
              void panels[n].offsetWidth;
              panels[n].classList.add("is-entering");
              panels[n].querySelectorAll("video[data-yap-autoplay]").forEach(playIfVisible);
            }
          }
        });
        if (focus) tabs[i].focus();
        wrap.dispatchEvent(new CustomEvent("yap:tab", { detail: i }));
      }
      tabs.forEach(function (t, i) {
        t.addEventListener("click", function () { select(i); });
        t.addEventListener("keydown", function (e) {
          var k = e.key, n = null;
          if (k === "ArrowRight") n = (i + 1) % tabs.length;
          if (k === "ArrowLeft") n = (i - 1 + tabs.length) % tabs.length;
          if (k === "Home") n = 0;
          if (k === "End") n = tabs.length - 1;
          if (n !== null) { e.preventDefault(); select(n, true); }
        });
      });
      select(0);
    });
  }

  /* ---------- Spec search: [data-yap-spec-search] filters rows in its section ---------- */
  function initSpecSearch(root) {
    root.querySelectorAll("[data-yap-spec-search]:not([data-yap-ready])").forEach(function (input) {
      input.setAttribute("data-yap-ready", "");
      var scope = input.closest("[data-yap-specs]");
      var empty = scope.querySelector("[data-yap-spec-empty]");
      var tabsWrap = scope.querySelector("[data-yap-tabs]");
      input.addEventListener("input", function () {
        var q = input.value.trim().toLowerCase();
        scope.classList.toggle("is-searching", q.length > 0);
        var hits = 0;
        scope.querySelectorAll("[data-yap-spec-row]").forEach(function (row) {
          var match = !q || row.textContent.toLowerCase().indexOf(q) !== -1;
          row.hidden = !match;
          if (match && q) hits++;
        });
        scope.querySelectorAll('[role="tabpanel"]').forEach(function (p) {
          if (q) {
            p.hidden = !p.querySelector("[data-yap-spec-row]:not([hidden])");
          }
        });
        if (!q && tabsWrap) {
          var sel = tabsWrap.querySelector('[role="tab"][aria-selected="true"]');
          var idx = [].indexOf.call(tabsWrap.querySelectorAll('[role="tab"]'), sel);
          scope.querySelectorAll('[role="tabpanel"]').forEach(function (p, n) { p.hidden = n !== idx; });
        }
        if (empty) empty.hidden = !(q && hits === 0);
      });
    });
  }

  /* ---------- Count-up: [data-yap-count] ---------- */
  function countUp(el) {
    var to = parseFloat(el.getAttribute("data-yap-count"));
    var dec = (el.getAttribute("data-yap-count").split(".")[1] || "").length;
    var fmt = function (n) { return n.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec }); };
    if (reduce || isNaN(to)) { el.textContent = isNaN(to) ? el.textContent : fmt(to); return; }
    var start = null, dur = 1400;
    function step(ts) {
      if (!start) start = ts;
      var t = Math.min((ts - start) / dur, 1);
      var e = 1 - Math.pow(1 - t, 3);
      el.textContent = fmt(to * e);
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* ---------- Videos: play only while visible ---------- */
  var vio = "IntersectionObserver" in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var v = e.target;
      if (e.isIntersecting && !reduce && v.offsetParent !== null) { v.play().catch(function () {}); }
      else { v.pause(); }
    });
  }, { threshold: 0.25 }) : null;
  function playIfVisible(v) { if (phone) return; if (vio) { vio.unobserve(v); vio.observe(v); } }

  function initViewWatchers(root) {
    root.querySelectorAll("video[data-yap-autoplay]:not([data-yap-ready])").forEach(function (v) {
      v.setAttribute("data-yap-ready", "");
      v.muted = true;
      if (reduce || phone) { v.removeAttribute("autoplay"); v.pause(); return; } /* phones: poster only, no video download (2026-09-28) */
      if (vio) vio.observe(v); else v.play().catch(function () {});
    });
    var cio = "IntersectionObserver" in window ? new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (e) { if (e.isIntersecting) { countUp(e.target); obs.unobserve(e.target); } });
    }, { threshold: 0.6 }) : null;
    root.querySelectorAll("[data-yap-count]:not([data-yap-ready])").forEach(function (el) {
      el.setAttribute("data-yap-ready", "");
      if (cio) cio.observe(el); else countUp(el);
    });
  }

  /* ---------- Lightbox: [data-yap-lightbox] groups of [data-yap-lb-item] ---------- */
  var lb = null;
  function buildLightbox() {
    if (lb) return lb;
    lb = document.createElement("div");
    lb.className = "yap-lb";
    lb.setAttribute("role", "dialog");
    lb.setAttribute("aria-modal", "true");
    lb.setAttribute("aria-label", "Image viewer");
    lb.hidden = true;
    lb.innerHTML =
      '<button class="yap-lb__close" type="button" aria-label="Close">&times;</button>' +
      '<button class="yap-lb__nav yap-lb__prev" type="button" aria-label="Previous image">&#8249;</button>' +
      '<figure class="yap-lb__fig"><img alt=""><figcaption></figcaption></figure>' +
      '<button class="yap-lb__nav yap-lb__next" type="button" aria-label="Next image">&#8250;</button>' +
      '<p class="yap-lb__count" aria-live="polite"></p>';
    document.body.appendChild(lb);
    return lb;
  }
  function initLightbox(root) {
    root.querySelectorAll("[data-yap-lightbox]:not([data-yap-ready])").forEach(function (group) {
      group.setAttribute("data-yap-ready", "");
      var items = [].slice.call(group.querySelectorAll("[data-yap-lb-item]"));
      var idx = 0, opener = null;
      function show(i) {
        idx = (i + items.length) % items.length;
        var it = items[idx];
        var img = lb.querySelector("img");
        img.src = it.getAttribute("data-yap-lb-src");
        img.alt = it.getAttribute("data-yap-lb-alt") || "";
        lb.querySelector("figcaption").textContent = it.getAttribute("data-yap-lb-alt") || "";
        lb.querySelector(".yap-lb__count").textContent = (idx + 1) + " / " + items.length;
      }
      function close() {
        lb.hidden = true;
        document.documentElement.classList.remove("yap-lb-open");
        document.removeEventListener("keydown", onKey);
        if (opener) opener.focus();
      }
      function onKey(e) {
        if (e.key === "Escape") close();
        if (e.key === "ArrowRight") show(idx + 1);
        if (e.key === "ArrowLeft") show(idx - 1);
      }
      items.forEach(function (it, i) {
        it.addEventListener("click", function (e) {
          e.preventDefault();
          buildLightbox();
          opener = it;
          lb.querySelector(".yap-lb__close").onclick = close;
          lb.querySelector(".yap-lb__prev").onclick = function () { show(idx - 1); };
          lb.querySelector(".yap-lb__next").onclick = function () { show(idx + 1); };
          lb.onclick = function (ev) { if (ev.target === lb) close(); };
          show(i);
          lb.hidden = false;
          document.documentElement.classList.add("yap-lb-open");
          document.addEventListener("keydown", onKey);
          lb.querySelector(".yap-lb__close").focus();
        });
      });
    });
  }

  /* ---------- System builder: [data-yap-builder] ---------- */
  function initBuilder(root) {
    root.querySelectorAll("[data-yap-builder]:not([data-yap-ready])").forEach(function (b) {
      b.setAttribute("data-yap-ready", "");
      var totalEl = b.querySelector("[data-yap-total]");
      var countEl = b.querySelector("[data-yap-seasons]");
      var addBtn = b.querySelector("[data-yap-builder-add]");
      var status = b.querySelector("[data-yap-builder-status]");
      var months = [].slice.call(b.querySelectorAll("[data-yap-month]"));
      var opts = [].slice.call(b.querySelectorAll("[data-yap-opt]"));
      var shown = 0;

      function animateTotal(to) {
        var from = shown; shown = to;
        if (reduce) { totalEl.textContent = money(to); return; }
        var start = null;
        function step(ts) {
          if (!start) start = ts;
          var t = Math.min((ts - start) / 500, 1);
          totalEl.textContent = money(from + (to - from) * (1 - Math.pow(1 - t, 3)));
          if (t < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      }

      function update() {
        var total = 0, jobs = 0, covered = {}, buyable = 0, pending = 0;
        opts.forEach(function (o) {
          var on = o.querySelector("input").checked;
          o.classList.toggle("is-on", on);
          b.querySelectorAll('[data-yap-stack="' + o.getAttribute("data-yap-opt") + '"]').forEach(function (s) { s.classList.toggle("is-on", on); });
          if (!on) return;
          total += parseInt(o.getAttribute("data-price"), 10) || 0;
          if (o.getAttribute("data-kind") === "module") jobs++;
          (o.getAttribute("data-months") || "").split(",").forEach(function (m) {
            m = m.trim(); if (m) covered[m] = (covered[m] || []).concat(o.getAttribute("data-color") || "");
          });
          if (o.getAttribute("data-variant")) buyable++; else pending++;
        });
        months.forEach(function (m) {
          var list = covered[m.getAttribute("data-yap-month")] || [];
          m.classList.toggle("is-on", list.length > 0);
          m.style.setProperty("--yap-month-color", list[list.length - 1] || "transparent");
        });
        animateTotal(total);
        if (countEl) countEl.textContent = jobs;
        if (addBtn) {
          addBtn.disabled = buyable === 0;
          addBtn.textContent = buyable === 0 ? "Choose a module" : "Add " + buyable + " item" + (buyable > 1 ? "s" : "") + " to cart";
        }
        if (status) status.textContent = pending ? pending + " selected item" + (pending > 1 ? "s are" : " is") + " not yet available to order online. Call us to reserve." : "";
      }

      opts.forEach(function (o) {
        var input = o.querySelector("input");
        if (input) input.addEventListener("change", update);
      });

      if (addBtn) addBtn.addEventListener("click", function () {
        var items = opts.filter(function (o) { return o.querySelector("input").checked && o.getAttribute("data-variant"); })
          .map(function (o) { return { id: parseInt(o.getAttribute("data-variant"), 10), quantity: 1 }; });
        if (!items.length) return;
        addBtn.disabled = true;
        addBtn.textContent = "Adding...";
        fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : "/") + "cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: items })
        }).then(function (r) {
          if (!r.ok) throw new Error("cart");
          window.location.href = (window.Shopify && Shopify.routes ? Shopify.routes.root : "/") + "cart";
        }).catch(function () {
          addBtn.disabled = false;
          addBtn.textContent = "Try again";
          if (status) status.textContent = "Something went wrong adding to cart. Please call +1 423-927-3776 and we will place the order for you.";
        });
      });
      update();
    });
  }

  /* ---------- Sticky add to cart ---------- */
  function initSticky(root) {
    var bar = root.querySelector("[data-yap-sticky]:not([data-yap-ready])");
    if (!bar) return;
    bar.setAttribute("data-yap-ready", "");
    var target = document.querySelector('form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"]');
    if (!target || !("IntersectionObserver" in window)) return;
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var show = !e.isIntersecting && e.boundingClientRect.top < 0;
        bar.classList.toggle("is-visible", show);
        bar.setAttribute("aria-hidden", show ? "false" : "true");
        bar.querySelectorAll("a, button").forEach(function (el) { el.tabIndex = show ? 0 : -1; });
      });
    }, { threshold: 0 }).observe(target);
  }

  /* ---------- Hotspots on the anatomy image ---------- */
  function initHotspots(root) {
    root.querySelectorAll("[data-yap-hotspots]:not([data-yap-ready])").forEach(function (wrap) {
      wrap.setAttribute("data-yap-ready", "");
      var dots = [].slice.call(wrap.querySelectorAll("[data-yap-dot]"));
      var cards = [].slice.call(wrap.querySelectorAll("[data-yap-dot-card]"));
      function pick(i) {
        dots.forEach(function (d, n) { d.setAttribute("aria-expanded", n === i ? "true" : "false"); d.classList.toggle("is-on", n === i); });
        cards.forEach(function (c, n) { c.hidden = n !== i; });
      }
      dots.forEach(function (d, i) {
        d.addEventListener("click", function () { pick(i); });
        d.addEventListener("mouseenter", function () { pick(i); });
      });
      if (dots.length) pick(0);
    });
  }

  function boot(root) {
    root = root || document;
    initTabs(root);
    initSpecSearch(root);
    initViewWatchers(root);
    initLightbox(root);
    initBuilder(root);
    initSticky(root);
    initHotspots(root);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { boot(); });
  else boot();
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
